import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Wrapper,
  ScrollArea,
  Item,
  Spacer,
  Dots,
  Dot,
} from "./StepScrollList.styles";

const ANIMATION_LOCK_MS = 400;

interface StepScrollListProps {
  children: React.ReactNode;
  visibleItems?: number;
  onScrollPresenceChange?: (hasScroll: boolean) => void;
}

const StepScrollList: React.FC<StepScrollListProps> = ({
  children,
  visibleItems = 2,
  onScrollPresenceChange,
}) => {
  const items = React.Children.toArray(children);
  const count = items.length;
  const effectiveVisibleItems = count <= 1 ? 1 : Math.max(1, visibleItems);

  const scrollRef = useRef<HTMLDivElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const animatingRef = useRef(false);
  const animationTimerRef = useRef<number | null>(null);
  const activeIndexRef = useRef(0);
  const hasScrollRef = useRef(false);
  const userInteractedRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Высота нижнего спейсера: столько места нужно, чтобы поднять последний
  // элемент в центр области — половина «недостающей» до неё высоты.
  const [spacerHeight, setSpacerHeight] = useState(0);

  itemRefs.current.length = count;

  const setActive = useCallback((index: number) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = scrollRef.current;
      const target = itemRefs.current[index];
      if (!container || !target) return;

      setActive(index);

      // Последний элемент прокручиваем в центр (в начало он встать не может —
      // под ним нет контента), остальные — в верх области.
      const isLast = index === itemRefs.current.length - 1;
      const top = isLast
        ? target.offsetTop - (container.clientHeight - target.offsetHeight) / 2
        : target.offsetTop;

      animatingRef.current = true;
      if (animationTimerRef.current !== null) {
        window.clearTimeout(animationTimerRef.current);
      }
      container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      animationTimerRef.current = window.setTimeout(() => {
        animatingRef.current = false;
        animationTimerRef.current = null;
      }, ANIMATION_LOCK_MS);
    },
    [setActive]
  );

  // Колесо перехватываем вручную (не через onWheel), чтобы отключить нативную
  // инерционную прокрутку и сдвигать список строго на один элемент за шаг.
  // Слушаем и саму область, и гутер с точками: над графиками колесо занято их
  // собственным зумом, поэтому гутер — основная зона прокрутки списка.
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (animatingRef.current) return;
      if (Math.abs(event.deltaY) < 1) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const next = activeIndexRef.current + direction;
      if (next < 0 || next > itemRefs.current.length - 1) return;
      scrollToIndex(next);
    };

    const targets = [scrollRef.current, gutterRef.current].filter(
      (el): el is HTMLDivElement => el !== null
    );
    targets.forEach((el) =>
      el.addEventListener("wheel", handleWheel, { passive: false })
    );
    return () =>
      targets.forEach((el) => el.removeEventListener("wheel", handleWheel));
  }, [scrollToIndex, count]);

  // Возврат области прокрутки в начало — пока пользователь не листал список сам,
  // любой сдвиг вызван перерасчётом вёрстки, а не его действием.
  const keepAtStart = useCallback(() => {
    if (userInteractedRef.current || animatingRef.current) return;
    const container = scrollRef.current;
    if (!container) return;

    if (container.scrollTop !== 0) container.scrollTop = 0;
    if (activeIndexRef.current !== 0) setActive(0);
  }, [setActive]);

  // Ручная/тач-прокрутка (снап) — подтягиваем активную точку к ближайшему элементу.
  const handleScroll = useCallback(() => {
    if (animatingRef.current) return;
    if (!userInteractedRef.current) {
      keepAtStart();
      return;
    }
    const container = scrollRef.current;
    if (!container) return;

    // Ищем элемент, чья «целевая» позиция прокрутки ближе всего к текущей.
    // Целевая позиция считается так же, как в scrollToIndex: верх — для обычных
    // элементов, центр — для последнего.
    const { scrollTop, clientHeight } = container;
    const lastIndex = itemRefs.current.length - 1;
    let nearest = 0;
    let bestDistance = Infinity;
    itemRefs.current.forEach((el, index) => {
      if (!el) return;
      const aligned =
        index === lastIndex
          ? el.offsetTop - (clientHeight - el.offsetHeight) / 2
          : el.offsetTop;
      const distance = Math.abs(Math.max(0, aligned) - scrollTop);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = index;
      }
    });
    if (nearest !== activeIndexRef.current) setActive(nearest);
  }, [keepAtStart, setActive]);

  // Пересчёт высоты нижнего спейсера. Спейсер нужен, чтобы последний элемент
  // можно было прокрутить в центр: место под ним = (высота области − высота
  // элемента) / 2. Если элемент выше области — спейсер не нужен.
  const measureSpacer = useCallback(() => {
    const container = scrollRef.current;
    const last = itemRefs.current[itemRefs.current.length - 1];
    if (!container || !last) {
      setSpacerHeight(0);
      return;
    }
    const next = Math.max(0, (container.clientHeight - last.offsetHeight) / 2);
    setSpacerHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, []);

  const notifyScrollPresence = useCallback(() => {
    const container = scrollRef.current;
    const hasScroll = container
      ? container.scrollHeight > container.clientHeight + 1
      : false;

    if (hasScrollRef.current === hasScroll) return;

    hasScrollRef.current = hasScroll;
    onScrollPresenceChange?.(hasScroll);
  }, [onScrollPresenceChange]);

  // Габариты меняются при загрузке данных, ресайзе окна и авто-масштабе графиков —
  // следим за областью и элементами через ResizeObserver.
  useLayoutEffect(() => {
    measureSpacer();
    notifyScrollPresence();
    const container = scrollRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      measureSpacer();
      notifyScrollPresence();
      keepAtStart();
    });
    observer.observe(container);
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [count, keepAtStart, measureSpacer, notifyScrollPresence]);

  // Отмечаем момент, когда пользователь сам начал листать список: с этого
  // момента удержание в начале (keepAtStart) больше не вмешивается.
  useEffect(() => {
    const markInteracted = () => {
      userInteractedRef.current = true;
    };

    const targets = [scrollRef.current, gutterRef.current].filter(
      (el): el is HTMLDivElement => el !== null
    );
    const events: Array<keyof HTMLElementEventMap> = [
      "wheel",
      "pointerdown",
      "touchstart",
      "keydown",
    ];
    targets.forEach((el) =>
      events.forEach((event) =>
        el.addEventListener(event, markInteracted, { passive: true })
      )
    );
    return () =>
      targets.forEach((el) =>
        events.forEach((event) =>
          el.removeEventListener(event, markInteracted)
        )
      );
  }, [count]);

  // Пока пользователь не пролистал список сам, держим его в начале. Одного
  // сброса при монтировании не хватает: графики догружают данные и меняют
  // высоту заметно позже, а mandatory scroll-snap на каждое такое изменение
  // может увести область прокрутки с первого элемента.
  useLayoutEffect(keepAtStart);

  // Если число элементов изменилось (загрузка/скрытие логов) — держим индекс в границах.
  useLayoutEffect(() => {
    if (count === 0) {
      setActive(0);
      return;
    }
    if (activeIndexRef.current > count - 1) {
      scrollToIndex(count - 1);
    }
  }, [count, scrollToIndex, setActive]);

  useEffect(
    () => () => {
      if (animationTimerRef.current !== null) {
        window.clearTimeout(animationTimerRef.current);
      }
    },
    []
  );

  return (
    <Wrapper $visibleItems={effectiveVisibleItems}>
      <ScrollArea ref={scrollRef} onScroll={handleScroll}>
        {items.map((child, index) => (
          <Item
            key={(child as any)?.key ?? index}
            $center={index === count - 1}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
          >
            {child}
          </Item>
        ))}
        {spacerHeight > 0 && (
          <Spacer aria-hidden style={{ height: spacerHeight }} />
        )}
      </ScrollArea>

      {count > 2 && (
        <Dots ref={gutterRef}>
          {items.map((child, index) => (
            <Dot
              key={(child as any)?.key ?? index}
              type="button"
              $active={index === activeIndex}
              aria-label={`Перейти к элементу ${index + 1}`}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </Dots>
      )}
    </Wrapper>
  );
};

export default StepScrollList;
